import { useRef, useState } from "react";
import { ArrayInput, Button, Datagrid, Edit, List, SimpleForm, SimpleFormIterator, TextField, TextInput, useNotify, useRecordContext, useRefresh } from "react-admin"
import { apiEndpoint, httpClient } from "../httpClient";

export const ClinicList = () => (
    <List>
        <Datagrid>
            <TextField source="name" label="Название" />
        </Datagrid>
    </List>
);

const ClinicTitle = () => {
  const record = useRecordContext();
  return <span>{record ? record.name : "Клиника"}</span>;
};

// The emblem is compressed in the browser until it fits: 700 KB is ~950 KB as base64 JSON,
// within the default 1 MB request limit of nginx (the API itself accepts up to 1 MB)
const LOGO_MAX_BYTES = 700 * 1024;
const LOGO_SIZES = [512, 384, 256];
const LOSSY_QUALITIES = [0.9, 0.8, 0.7, 0.6];

const dataUrlBytes = dataUrl => Math.ceil((dataUrl.length - dataUrl.indexOf(',') - 1) * 3 / 4);

const loadImage = file => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => {
    URL.revokeObjectURL(url);
    resolve(image);
  };
  image.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new Error('Не удалось открыть изображение'));
  };
  image.src = url;
});

const drawScaled = (image, size, background) => {
  const scale = Math.min(1, size / Math.max(image.width, image.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(image.width * scale);
  canvas.height = Math.round(image.height * scale);
  const context = canvas.getContext('2d');
  if (background) {
    context.fillStyle = background;
    context.fillRect(0, 0, canvas.width, canvas.height);
  }
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas;
};

// PNG keeps transparency; if it is too big, a lossy format with lower quality, then smaller sizes.
// Safari can't encode WebP (toDataURL falls back to PNG): JPEG on white there, as JPEG has no transparency
const toLogoDataUrl = async file => {
  const image = await loadImage(file);
  const png = drawScaled(image, LOGO_SIZES[0]).toDataURL('image/png');
  if (dataUrlBytes(png) <= LOGO_MAX_BYTES) return png;
  const webp = document.createElement('canvas').toDataURL('image/webp').startsWith('data:image/webp');
  let smallest = png;
  for (const size of LOGO_SIZES) {
    const canvas = drawScaled(image, size, webp ? null : '#ffffff');
    for (const quality of LOSSY_QUALITIES) {
      const dataUrl = canvas.toDataURL(webp ? 'image/webp' : 'image/jpeg', quality);
      if (dataUrlBytes(dataUrl) <= LOGO_MAX_BYTES) return dataUrl;
      if (dataUrlBytes(dataUrl) < dataUrlBytes(smallest)) smallest = dataUrl;
    }
  }
  return smallest;
};

const ClinicLogoInput = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const fileInput = useRef(null);
  const [busy, setBusy] = useState(false);

  if (!record) return null;
  const logoUrl = `${apiEndpoint}/clinics/${record.id}/logo`;

  const request = async (options, success) => {
    setBusy(true);
    try {
      await httpClient(logoUrl, options);
      notify(success, { type: 'success' });
      refresh();
    } catch (error) {
      notify(error?.body?.detail || error.message || 'Ошибка при сохранении', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const upload = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    let image;
    try {
      image = await toLogoDataUrl(file);
    } catch (error) {
      notify(error.message, { type: 'error' });
      return;
    }
    if (dataUrlBytes(image) > LOGO_MAX_BYTES) {
      notify('Изображение слишком большое, выберите другое', { type: 'error' });
      return;
    }
    await request({ method: 'PUT', body: JSON.stringify({ image }) }, 'Эмблема сохранена');
  };

  const logoBox = { width: 96, height: 96, borderRadius: 8, border: '1px solid #e0e0e0', flexShrink: 0 };

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.6)', marginBottom: 4 }}>Эмблема клиники</div>
      <div style={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.6)', marginBottom: 12 }}>
        Показывается пациентам в приложении в списке клиник и у врачей клиники.
        Файл PNG, JPEG или WebP, лучше квадратный, на прозрачном или белом фоне.
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {record.logo_url ? (
          <img src={record.logo_url} alt="Эмблема клиники" style={{ ...logoBox, objectFit: 'contain' }} />
        ) : (
          <div style={{ ...logoBox, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontSize: 12, color: '#808080', borderStyle: 'dashed' }}>
            Нет эмблемы
          </div>
        )}
        <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={upload} />
        <Button label={record.logo_url ? 'Заменить эмблему' : 'Загрузить эмблему'} disabled={busy} onClick={() => fileInput.current?.click()} />
        {record.logo_url ? (
          <Button label="Удалить" disabled={busy} onClick={() => request({ method: 'DELETE' }, 'Эмблема удалена')} />
        ) : null}
      </div>
    </div>
  );
};

export const ClinicEdit = () => (
    <Edit title={<ClinicTitle />}>
        <SimpleForm>
            <ClinicLogoInput />
            <TextInput source="name" label="Название клиники" />
            <TextInput source="address" label="Адрес" />
            <TextInput source="working_hours" label="Режим работы" />
            <ArrayInput source="appointment_extra_questions" label="Дополнительные вопросы при записи на прием">
                <SimpleFormIterator inline>
                    <TextInput source="text" label="Текст вопроса" />
                </SimpleFormIterator>
            </ArrayInput>
        </SimpleForm>
    </Edit>
);
